import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import {
  Upload, Loader2, Check, AlertCircle, X, Gift, Sparkles,
  Clock, Camera, Truck, Shield, ChevronRight, ChevronLeft,
  Plus, Minus, Image as ImageIcon, CreditCard, MapPin
} from 'lucide-react';
import SEO from '../components/SEO';
import { useLang } from '../context/LangContext';

import { API } from '../api/config';
import { customOrdersApi, cmsApi, settingsApi, uploadsApi } from '../api/requests';
const iconMap = { gift: Gift, sparkles: Sparkles, clock: Clock, camera: Camera };

export default function CustomOrder() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [cms, setCms] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);
  const [figurineType, setFigurineType] = useState('single');
  const [selectedSize, setSelectedSize] = useState('small');
  const [extraPersons, setExtraPersons] = useState(0);
  const [addFrame, setAddFrame] = useState(false);
  const [complexity, setComplexity] = useState('standard');
  const [estimate, setEstimate] = useState(null);
  const [form, setForm] = useState({
    customerName: '', customerEmail: '', customerPhone: '', notes: '',
    shippingCountry: '', shippingCity: '', shippingPostalCode: '',
    shippingStreet: '', shippingApartment: '', shippingNotes: ''
  });
  const [step, setStep] = useState(1); // 1: configure, 2: review+pay
  const fileInputRef = useRef(null);

  useEffect(() => {
    Promise.all([
      cmsApi.getSection('customOrder').catch(() => ({ data: null })),
      settingsApi.getPublic().catch(() => ({ data: null })),
    ]).then(([cmsRes, settingsRes]) => {
      setCms(cmsRes.data);
      setSettings(settingsRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const fetchEstimate = useCallback(async () => {
    try {
      const { data } = await customOrdersApi.estimate({
        figurineType, selectedSize, extraPersons, addFrame, complexityLevel: complexity
      });
      setEstimate(data);
    } catch { /* silent */ }
  }, [figurineType, selectedSize, extraPersons, addFrame, complexity]);

  useEffect(() => { fetchEstimate(); }, [fetchEstimate]);

  // CMS defaults
  const d = cms || {};
  const badge = d.badge || t('customOrder.badge');
  const title = d.title || t('customOrder.title');
  const subtitle = d.subtitle || t('customOrder.subtitle');
  const priceTeaser = d.priceTeaser || 'From 49';
  const benefits = d.benefits || [
    { icon: 'gift', text: 'Perfect gift idea' },
    { icon: 'sparkles', text: 'Handmade & detailed' },
    { icon: 'clock', text: 'Fast production (7-10 days)' },
    { icon: 'camera', text: 'Made from your real photos' }
  ];
  const hiw = d.howItWorks || { title: 'How It Works', steps: [] };
  const delivery = d.deliveryInfo || { title: 'Delivery', items: [] };
  const upload = d.uploadSection || { title: 'Your Photos', maxPhotos: 5 };
  const ftypes = d.figurineTypes?.types || [
    { id: 'single', label: 'Single Person', description: 'One person figurine', basePrice: 49 },
    { id: 'couple', label: 'Couple', description: 'Two people together', basePrice: 89 },
    { id: 'family', label: 'Family', description: '3-4 people group', basePrice: 129 },
    { id: 'scene', label: 'Custom Scene', description: 'Unique themed scene', basePrice: 159 }
  ];
  const sizes = d.sizeOptions?.sizes || [
    { id: 'small', label: 'Small', size: '10 cm', surcharge: 0 },
    { id: 'medium', label: 'Medium', size: '15 cm', surcharge: 20 },
    { id: 'large', label: 'Large', size: '20 cm', surcharge: 50 }
  ];
  const extras = d.extras || {};
  const cLevels = extras.complexityLevels || [
    { id: 'standard', label: 'Standard Detail', surcharge: 0 },
    { id: 'high', label: 'High Detail', surcharge: 30 },
    { id: 'premium', label: 'Premium / Realistic', surcharge: 60 }
  ];
  const maxPhotos = upload.maxPhotos || 5;

  // Upload through backend
  const uploadImage = async (file) => {
    const { data } = await uploadsApi.uploadPublic(file);
    return data.url.startsWith('http') ? data.url : `${API}${data.url}`;
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = maxPhotos - images.length;
    if (remaining <= 0) { setError(`Maximum ${maxPhotos} photos`); return; }
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024;
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    setError('');
    try {
      for (const file of toUpload) {
        if (!allowed.includes(file.type)) { setError(`"${file.name}": use JPG, PNG, or WebP.`); continue; }
        if (file.size > maxSize) { setError(`"${file.name}" exceeds 10MB.`); continue; }
        const url = await uploadImage(file);
        setImages(prev => [...prev, { url, name: file.name }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (idx) => setImages(prev => prev.filter((_, i) => i !== idx));

  const validateForm = () => {
    if (images.length === 0) return t('customOrder.validationPhoto');
    if (!form.customerName.trim()) return t('customOrder.validationName');
    if (!form.customerEmail.trim() || !form.customerEmail.includes('@')) return t('customOrder.validationEmail');
    if (!form.shippingCountry.trim()) return t('customOrder.validationCountry');
    if (!form.shippingCity.trim()) return t('customOrder.validationCity');
    if (!form.shippingPostalCode.trim()) return t('customOrder.validationPostal');
    if (!form.shippingStreet.trim()) return t('customOrder.validationStreet');
    return null;
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    const err = validateForm();
    if (err) { setError(err); return; }
    setError('');
    setStep(2);
  };

  const buildOrderPayload = (paypalOrderId) => ({
    customerName: form.customerName,
    customerEmail: form.customerEmail,
    customerPhone: form.customerPhone || null,
    figurineType, selectedSize, extraPersons, addFrame,
    complexityLevel: complexity,
    imageUrls: images.map(img => img.url),
    notes: form.notes || null,
    paypalOrderId: paypalOrderId || '',
    shippingCountry: form.shippingCountry,
    shippingCity: form.shippingCity,
    shippingPostalCode: form.shippingPostalCode,
    shippingStreet: form.shippingStreet,
    shippingApartment: form.shippingApartment || null,
    shippingNotes: form.shippingNotes || null,
  });

  const submitOrder = async (paypalOrderId) => {
    setSubmitting(true);
    setError('');
    try {
      await customOrdersApi.create(buildOrderPayload(paypalOrderId));
      navigate('/custom-order/success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit order.');
    } finally { setSubmitting(false); }
  };

  // PayPal
  const paypalConfigured = Boolean(settings?.paypalClientId);

  const createPayPalOrder = (data, actions) => {
    if (!estimate) return;
    return actions.order.create({
      purchase_units: [{
        description: `Custom Figurine - ${estimate.baseLabel}`,
        amount: { currency_code: settings?.paypalCurrency || 'EUR', value: estimate.total.toFixed(2) },
      }],
    });
  };

  const onPayPalApprove = async (data, actions) => {
    try {
      const details = await actions.order.capture();
      await submitOrder(details.id);
    } catch { setError('Payment processing failed. Please try again.'); }
  };

  if (loading) return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="w-8 h-8 text-indigo-400 animate-spin" /></div>;

  return (
    <div data-testid="custom-order-page" className="min-h-screen bg-[#0A0A0A]">
      <SEO page="customOrder" lang={lang} path="/custom-order" />
      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-500/[0.08] via-transparent to-transparent" />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <span className="inline-block text-xs tracking-[0.25em] uppercase font-bold text-indigo-400 mb-4">{badge}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Outfit'] font-light tracking-tighter text-white mb-5">{title}</h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-8">{subtitle}</p>
          <div className="inline-flex items-baseline gap-1 px-6 py-3 rounded-full bg-white/5 border border-white/10">
            <span className="text-3xl font-['Outfit'] font-semibold text-white">{priceTeaser}</span>
            <span className="text-zinc-400 text-lg">EUR</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto mt-10">
            {benefits.map((b, i) => {
              const Icon = iconMap[b.icon] || Sparkles;
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                  <Icon className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                  <span className="text-sm text-zinc-300">{b.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      {hiw.steps?.length > 0 && (
        <section className="py-16 bg-[#0d0d0d]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-['Outfit'] font-light text-white text-center mb-12">{hiw.title}</h2>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-center gap-6 md:gap-0">
              {hiw.steps.map((s, i) => (
                <div key={i} className="flex items-center gap-4 md:gap-0">
                  <div className="flex flex-col items-center text-center max-w-[200px]">
                    <div className="w-14 h-14 rounded-full bg-indigo-500/15 border border-indigo-500/30 flex items-center justify-center mb-4">
                      <span className="text-xl font-['Outfit'] font-bold text-indigo-400">{s.step}</span>
                    </div>
                    <h3 className="text-white font-medium text-sm mb-1">{s.title}</h3>
                    <p className="text-zinc-500 text-xs leading-relaxed">{s.description}</p>
                  </div>
                  {i < hiw.steps.length - 1 && <ChevronRight className="hidden md:block w-5 h-5 text-zinc-700 mx-6 flex-shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Step indicator */}
      <section className="pt-12 pb-4">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-center gap-4">
          <StepBadge num={1} label={t('customOrder.stepConfigure')} active={step === 1} />
          <ChevronRight className="w-4 h-4 text-zinc-600" />
          <StepBadge num={2} label={t('customOrder.stepPayment')} active={step === 2} />
        </div>
      </section>

      {/* Error banner */}
      <section className="max-w-5xl mx-auto px-4">
        {error && (
          <div data-testid="custom-order-error" className="flex items-center gap-3 p-4 mb-6 bg-red-500/10 border border-red-500/20 rounded-xl">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-sm">{error}</span>
            <button type="button" onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4 text-red-400" /></button>
          </div>
        )}
      </section>

      {/* Main content */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-4">
          {step === 1 ? (
            <form onSubmit={handleProceedToPayment}>
              <div className="grid lg:grid-cols-[1fr_340px] gap-8">
                <div className="space-y-8">
                  {/* Photos */}
                  <Card title={upload.title || t('customOrder.photos')} subtitle={upload.subtitle || t('customOrder.photosSubtitle', { max: maxPhotos })} badge={`${images.length}/${maxPhotos}`}>
                    <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="image/jpeg,image/png,image/webp" multiple className="hidden" />
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                      {images.map((img, i) => (
                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden group bg-zinc-900">
                          <img src={img.url} alt={img.name} className="w-full h-full object-cover" />
                          <button type="button" onClick={() => removeImage(i)} data-testid={`remove-photo-${i}`}
                            className="absolute top-1.5 right-1.5 w-6 h-6 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <X className="w-3.5 h-3.5 text-white" />
                          </button>
                        </div>
                      ))}
                      {images.length < maxPhotos && (
                        <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="add-photo-btn"
                          className="aspect-square rounded-xl border-2 border-dashed border-white/10 hover:border-indigo-500/50 flex flex-col items-center justify-center text-zinc-600 hover:text-indigo-400 transition-all cursor-pointer">
                          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : <><Upload className="w-6 h-6 mb-1" /><span className="text-[10px]">Add</span></>}
                        </button>
                      )}
                    </div>
                    {upload.tips?.length > 0 && (
                      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1">
                        {upload.tips.map((tip, i) => <span key={i} className="flex items-center gap-1.5 text-xs text-zinc-600"><Check className="w-3 h-3 text-indigo-500/60" />{tip}</span>)}
                      </div>
                    )}
                  </Card>

                  {/* Figurine Type */}
                  <Card title={d.figurineTypes?.title || t('customOrder.figurineType')}>
                    <div className="grid grid-cols-2 gap-3">
                      {ftypes.map((t) => (
                        <button key={t.id} type="button" onClick={() => setFigurineType(t.id)} data-testid={`type-${t.id}`}
                          className={`relative p-4 rounded-xl border-2 text-left transition-all ${figurineType === t.id ? 'border-indigo-500 bg-indigo-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/15'}`}>
                          {figurineType === t.id && <div className="absolute top-3 right-3 w-5 h-5 bg-indigo-500 rounded-full flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>}
                          <p className="text-white font-medium text-sm">{t.label}</p>
                          <p className="text-zinc-500 text-xs mt-0.5">{t.description}</p>
                          <p className="text-indigo-400 text-sm font-['Outfit'] font-semibold mt-2">from {t.basePrice} EUR</p>
                        </button>
                      ))}
                    </div>
                  </Card>

                  {/* Size */}
                  <Card title={d.sizeOptions?.title || t('customOrder.size')}>
                    <div className="grid grid-cols-3 gap-3">
                      {sizes.map((s) => (
                        <button key={s.id} type="button" onClick={() => setSelectedSize(s.id)} data-testid={`size-${s.id}`}
                          className={`p-4 rounded-xl border-2 text-center transition-all ${selectedSize === s.id ? 'border-indigo-500 bg-indigo-500/[0.08]' : 'border-white/[0.06] bg-white/[0.02] hover:border-white/15'}`}>
                          <p className="text-white font-medium text-sm">{s.label}</p>
                          <p className="text-zinc-500 text-xs">{s.size}</p>
                          <p className="text-zinc-400 text-xs mt-1">{s.surcharge === 0 ? t('customOrder.included') : `+${s.surcharge} EUR`}</p>
                        </button>
                      ))}
                    </div>
                  </Card>

                  {/* Extras */}
                  <Card title={extras.title || t('customOrder.extras')}>
                    <div className="space-y-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{extras.extraPersonLabel || t('customOrder.additionalPerson')}</p>
                          <p className="text-zinc-500 text-xs">+{extras.extraPersonPrice || 25} EUR {t('customOrder.each')}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button type="button" onClick={() => setExtraPersons(Math.max(0, extraPersons - 1))} disabled={extraPersons === 0} data-testid="extra-person-minus"
                            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white disabled:opacity-30 transition-colors"><Minus className="w-4 h-4" /></button>
                          <span data-testid="extra-persons-count" className="text-white font-mono w-6 text-center">{extraPersons}</span>
                          <button type="button" onClick={() => setExtraPersons(Math.min(10, extraPersons + 1))} data-testid="extra-person-plus"
                            className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"><Plus className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{extras.frameLabel || t('customOrder.displayFrame')}</p>
                          <p className="text-zinc-500 text-xs">+{(extras.framePrices || {})[selectedSize] || 20} EUR</p>
                        </div>
                        <button type="button" onClick={() => setAddFrame(!addFrame)} data-testid="toggle-frame"
                          className={`w-12 h-7 rounded-full transition-colors relative ${addFrame ? 'bg-indigo-500' : 'bg-zinc-700'}`}>
                          <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${addFrame ? 'left-[22px]' : 'left-0.5'}`} />
                        </button>
                      </div>
                      <div>
                        <p className="text-white text-sm mb-3">{t('customOrder.detailLevel')}</p>
                        <div className="grid grid-cols-3 gap-2">
                          {cLevels.map((c) => (
                            <button key={c.id} type="button" onClick={() => setComplexity(c.id)} data-testid={`complexity-${c.id}`}
                              className={`py-2.5 px-3 rounded-lg border text-center transition-all text-xs ${complexity === c.id ? 'border-indigo-500 bg-indigo-500/10 text-indigo-300' : 'border-white/[0.06] text-zinc-400 hover:border-white/15'}`}>
                              <span className="block">{c.label}</span>
                              {c.surcharge > 0 && <span className="block text-[10px] mt-0.5 opacity-60">+{c.surcharge} EUR</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Card>

                  {/* Contact Details */}
                  <Card title={t('customOrder.yourDetails')}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label={`${t('customOrder.fullName')} *`} value={form.customerName} onChange={v => setForm({...form, customerName: v})} testId="custom-order-name" placeholder="Your name" />
                      <Input label={`${t('customOrder.email')} *`} type="email" value={form.customerEmail} onChange={v => setForm({...form, customerEmail: v})} testId="custom-order-email" placeholder="your@email.com" />
                      <Input label={t('customOrder.phone')} type="tel" value={form.customerPhone} onChange={v => setForm({...form, customerPhone: v})} testId="custom-order-phone" placeholder="+49 123 456 7890" />
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-zinc-500 mb-1.5">{t('customOrder.specialRequests')}</label>
                        <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="custom-order-notes" rows={2} placeholder="Describe pose, outfit, accessories..."
                          className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white text-sm placeholder-zinc-600 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 outline-none transition-colors resize-none" />
                      </div>
                    </div>
                  </Card>

                  {/* Shipping */}
                  <Card title={t('customOrder.shippingAddress')} icon={<MapPin className="w-4 h-4 text-indigo-400" />}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Input label={`${t('customOrder.country')} *`} value={form.shippingCountry} onChange={v => setForm({...form, shippingCountry: v})} testId="shipping-country" placeholder="Germany" />
                      <Input label={`${t('customOrder.city')} *`} value={form.shippingCity} onChange={v => setForm({...form, shippingCity: v})} testId="shipping-city" placeholder="Berlin" />
                      <Input label={`${t('customOrder.postalCode')} *`} value={form.shippingPostalCode} onChange={v => setForm({...form, shippingPostalCode: v})} testId="shipping-postal" placeholder="10115" />
                      <Input label={`${t('customOrder.street')} *`} value={form.shippingStreet} onChange={v => setForm({...form, shippingStreet: v})} testId="shipping-street" placeholder="Hauptstr. 1" />
                      <Input label={t('customOrder.apartment')} value={form.shippingApartment} onChange={v => setForm({...form, shippingApartment: v})} testId="shipping-apt" placeholder="Apt 4B" />
                      <div className="sm:col-span-2">
                        <label className="block text-xs text-zinc-500 mb-1.5">{t('customOrder.deliveryNotes')}</label>
                        <textarea value={form.shippingNotes} onChange={e => setForm({...form, shippingNotes: e.target.value})} data-testid="shipping-notes" rows={2} placeholder="Ring doorbell, leave at door..."
                          className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white text-sm placeholder-zinc-600 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 outline-none transition-colors resize-none" />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Right Column — sticky price */}
                <div className="lg:sticky lg:top-24 lg:self-start space-y-6">
                  <PriceSummary estimate={estimate} />
                  <button type="submit" disabled={submitting || images.length === 0} data-testid="proceed-to-payment-btn"
                    className="w-full py-3.5 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2">
                    <CreditCard className="w-4 h-4" /><span>{t('customOrder.continueToPayment')}</span><ChevronRight className="w-4 h-4" />
                  </button>
                  {delivery.items?.length > 0 && (
                    <div className="bg-[#111] border border-white/[0.06] rounded-2xl p-6">
                      <div className="flex items-center gap-2 mb-4"><Truck className="w-4 h-4 text-indigo-400" /><h3 className="text-sm font-medium text-white">{t('customOrder.delivery')}</h3></div>
                      <ul className="space-y-2">{delivery.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs text-zinc-400"><Shield className="w-3 h-3 text-indigo-500/50 mt-0.5 flex-shrink-0" />{item}</li>)}</ul>
                    </div>
                  )}
                  <div className="bg-[#111] border border-white/[0.06] rounded-2xl p-6">
                    <div className="flex items-center gap-2 mb-3"><ImageIcon className="w-4 h-4 text-indigo-400" /><h3 className="text-sm font-medium text-white">{t('customOrder.photosStatus')}</h3></div>
                    <div className="flex gap-1.5">{Array.from({ length: maxPhotos }).map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i < images.length ? 'bg-indigo-500' : 'bg-zinc-800'}`} />)}</div>
                    <p className="text-xs text-zinc-500 mt-2">{images.length === 0 ? t('customOrder.noPhotos') : t('customOrder.photosUploaded', { count: images.length, max: maxPhotos })}</p>
                  </div>
                </div>
              </div>
            </form>
          ) : (
            /* Step 2: Review & Pay */
            <div className="grid lg:grid-cols-[1fr_340px] gap-8">
              <div className="space-y-6">
                <button type="button" onClick={() => { setStep(1); setError(''); }} data-testid="back-to-configure-btn"
                  className="flex items-center gap-2 text-sm text-indigo-400 hover:text-indigo-300 transition-colors"><ChevronLeft className="w-4 h-4" /><span>{t('customOrder.backToConfig')}</span></button>

                {/* Order review */}
                <Card title={t('customOrder.orderSummary')}>
                  <div className="mb-6">
                    <p className="text-zinc-400 text-sm mb-3">Photos ({images.length})</p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {images.map((img, i) => <img key={i} src={img.url} alt={img.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />)}
                    </div>
                  </div>
                  <div className="space-y-3 text-sm">
                    <Row label={t('customOrder.type')} value={estimate?.baseLabel} />
                    <Row label={t('customOrder.size')} value={estimate?.sizeLabel} />
                    {extraPersons > 0 && <Row label={t('customOrder.extraPersons')} value={extraPersons} />}
                    {addFrame && <Row label={t('customOrder.displayFrame')} value="Yes" />}
                    <Row label={t('customOrder.detailLevel')} value={estimate?.complexityLabel} />
                  </div>
                  <div className="border-t border-white/[0.06] mt-4 pt-4 space-y-2 text-sm">
                    <Row label={t('customOrder.customer')} value={form.customerName} />
                    <Row label={t('customOrder.email')} value={form.customerEmail} />
                    {form.customerPhone && <Row label={t('customOrder.phone')} value={form.customerPhone} />}
                    {form.notes && <div className="mt-2"><span className="text-zinc-400">{t('customOrder.notes')}:</span><p className="text-zinc-300 mt-1">{form.notes}</p></div>}
                  </div>
                  <div className="border-t border-white/[0.06] mt-4 pt-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2 mb-2"><MapPin className="w-3.5 h-3.5 text-indigo-400" /><span className="text-zinc-400 text-xs uppercase tracking-wider">{t('customOrder.shipping')}</span></div>
                    <p className="text-white text-sm">{form.shippingStreet}{form.shippingApartment ? `, ${form.shippingApartment}` : ''}</p>
                    <p className="text-white text-sm">{form.shippingPostalCode} {form.shippingCity}</p>
                    <p className="text-white text-sm">{form.shippingCountry}</p>
                    {form.shippingNotes && <p className="text-zinc-400 text-xs mt-1">"{form.shippingNotes}"</p>}
                  </div>
                </Card>

                {/* Payment */}
                <Card title={t('customOrder.payment')}>
                  {paypalConfigured ? (
                    <div data-testid="paypal-payment-section">
                      <p className="text-zinc-400 text-sm mb-4">{t('customOrder.paySecurely')}</p>
                      <PayPalScriptProvider options={{ clientId: settings.paypalClientId, currency: settings.paypalCurrency || 'EUR' }}>
                        <PayPalButtons
                          style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
                          disabled={!estimate || submitting}
                          forceReRender={[settings.paypalClientId, settings.paypalCurrency || 'EUR', estimate?.total || 0]}
                          createOrder={createPayPalOrder}
                          onApprove={onPayPalApprove}
                          onError={() => setError('Payment failed. Please try again.')}
                          onCancel={() => setError('Payment cancelled.')}
                        />
                      </PayPalScriptProvider>
                    </div>
                  ) : (
                    <div data-testid="paypal-not-configured" className="text-center py-4">
                      <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-3" />
                      <p className="text-white font-medium mb-2">{t('customOrder.paymentNotAvailable')}</p>
                      <p className="text-zinc-400 text-sm mb-5">{t('customOrder.paymentNotAvailableDesc')}</p>
                      <button type="button" disabled data-testid="paypal-disabled-btn"
                        className="px-6 py-3 bg-white/5 border border-white/10 text-zinc-500 font-medium rounded-xl cursor-not-allowed inline-flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        <span>{t('customOrder.continueToPayment')}</span>
                      </button>
                    </div>
                  )}
                </Card>
              </div>

              <div className="lg:sticky lg:top-24 lg:self-start">
                <PriceSummary estimate={estimate} />
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ---- Sub-components ---- */

function StepBadge({ num, label, active }) {
  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${active ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'text-zinc-500'}`}>
      <span className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-xs font-bold">{num}</span>
      <span>{label}</span>
    </div>
  );
}

function Card({ title, subtitle, badge, icon, children }) {
  return (
    <div className="bg-[#111] border border-white/[0.06] rounded-2xl p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <h2 className="text-lg font-['Outfit'] font-medium text-white">{title}</h2>
            {subtitle && <p className="text-zinc-500 text-sm mt-1">{subtitle}</p>}
          </div>
        </div>
        {badge && <span className="text-xs font-mono text-zinc-500 bg-white/5 px-3 py-1.5 rounded-full">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Input({ label, value, onChange, testId, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs text-zinc-500 mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/[0.08] rounded-lg text-white text-sm placeholder-zinc-600 focus:border-indigo-500/50 focus:ring-1 focus:ring-indigo-500/30 outline-none transition-colors" />
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between"><span className="text-zinc-400">{label}</span><span className="text-white">{value}</span></div>;
}

function PriceSummary({ estimate }) {
  if (!estimate) return null;
  return (
    <div data-testid="price-summary" className="bg-[#111] border border-white/[0.06] rounded-2xl p-6">
      <h3 className="text-sm font-medium text-zinc-400 mb-4">Estimated Price</h3>
      <div className="space-y-3">
        <div className="flex justify-between text-sm"><span className="text-zinc-400">{estimate.baseLabel}</span><span className="text-white">{estimate.base} EUR</span></div>
        {estimate.sizeSurcharge > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">{estimate.sizeLabel} size</span><span className="text-white">+{estimate.sizeSurcharge} EUR</span></div>}
        {estimate.extraPersonsCost > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">{estimate.extraPersons} extra person{estimate.extraPersons > 1 ? 's' : ''}</span><span className="text-white">+{estimate.extraPersonsCost} EUR</span></div>}
        {estimate.frameCost > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">Display frame</span><span className="text-white">+{estimate.frameCost} EUR</span></div>}
        {estimate.complexitySurcharge > 0 && <div className="flex justify-between text-sm"><span className="text-zinc-400">{estimate.complexityLabel}</span><span className="text-white">+{estimate.complexitySurcharge} EUR</span></div>}
        <div className="border-t border-white/[0.06] pt-3 mt-3">
          <div className="flex justify-between items-baseline">
            <span className="text-zinc-400 text-sm">Total</span>
            <span className="text-3xl font-['Outfit'] font-semibold text-white">{estimate.total}<span className="text-lg text-zinc-400"> EUR</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
